import { isEqualWith, isFunction, isEqual } from 'lodash-es';
import { F as FocusSource } from './focus-source.enum-fe7089b3.js';
import { a as BxfDatePickerDateFormatEnum, b as BxfDatePickerSeparatorsEnum } from './bxf-date-picker-separators.enum-a0b9baeb.js';

function getDataProps(props, nodeName) {
    const dataProps = {};
    Object.keys(props).forEach((key) => {
        if (!(typeof props[key] === 'string' ||
            typeof props[key] === 'number' ||
            typeof props[key] === 'boolean') ||
            key === 'contentEditable' ||
            key === 'contentEditable' ||
            key === 'class' ||
            key === 'style' ||
            key === 'title')
            return;
        if (props[key] != null) {
            dataProps[`data-bxf-${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}`] = String(props[key]);
        }
    });
    if (nodeName) {
        dataProps['data-bxf-node-name'] = nodeName;
    }
    return dataProps;
}

function getNodeByBxfNodeName(bxfNodeName, host) {
    const query = `[data-bxf-node-name="${bxfNodeName}"]`;
    const shadowQuery = host.shadowRoot && host.shadowRoot.querySelector(query);
    return host.querySelector(query) || shadowQuery;
}

function getState(context = this) {
    if (context === this) {
        return this;
    }
    return this[context];
}

let listeners = [];
const removeGlobalEventListener = (elm, type, listener, options) => {
    // Find the old listener
    const oldListener = listeners.find((_listener) => isEqualWith(listener, _listener.handler, (l1, l2) => {
        if (isFunction(l1) && isFunction(l2)) {
            return l1.toString() === l2.toString();
        }
        return isEqual(l1, l2);
    }) &&
        isEqual(_listener.options, options) &&
        isEqual(_listener.type, type));
    if (oldListener) {
        // Remove the old listener from element
        elm.removeEventListener(oldListener.type, oldListener.handler, oldListener.options);
        // Remove the old listener form the saved array
        listeners = listeners.filter((item) => item !== oldListener);
    }
};
const addGlobalEventListener = (elm, type, listener, options) => {
    // Remoeve the old listener
    removeGlobalEventListener(elm, type, listener, options);
    // Add new event listener to element
    elm.addEventListener(type, listener, options);
    // Add new event listener to saved array
    listeners.push({ type, handler: listener, options });
};

async function setState(state, context = this) {
    setTimeout(() => {
        if (context === this) {
            Object.keys(state).forEach((key) => (this[key] = state[key]));
        }
        else {
            this[context] = { ...this[context], ...state };
        }
        Promise.resolve();
    });
}

function withState(state, stateName, context) {
    const _state = {
        ...context[stateName],
        ...state,
        setState: setState.bind(context),
        getState: getState.bind(context),
        stateName,
    };
    return _state;
}

/**
 * Find the closest ancestor of a root that matches a given CSS selector, traversing the shadow dom.
 *
 * @export
 * @param {string} selector
 * @param {*} _root
 * @returns {Element}
 */
function closestShadowDom(selector, root) {
    let _root = root;
    if (!_root || _root === document || _root === window) {
        return null;
    }
    if (_root.assignedSlot) {
        _root = _root.assignedSlot;
    }
    const match = _root.closest(selector);
    return match || closestShadowDom(selector, _root.getRootNode().host);
}

function getFocusSource(event, keepMouseFocus) {
    if (!keepMouseFocus) {
        return event.pointerType === FocusSource.Touch
            ? FocusSource.Touch
            : FocusSource.Mouse;
    }
    return FocusSource.Keyboard;
}

const classes$5 = {
    CALENDAR: 'bxf-pill__calendar',
};
const constants = {
    DEFAULT_DATE_FORMAT: 'YYYY-MMM-DD',
    CALENDAR_DATE_FORMAT: BxfDatePickerDateFormatEnum.DD_MM_YYYY.replace(/\s/g, BxfDatePickerSeparatorsEnum.DASH),
};

const classes$4 = {
    CONTAINER: 'bxf-pill__container',
};

const classes$3 = {
    ICON: 'bxf-pill__icon',
};
// eslint-disable-next-line @typescript-eslint/prefer-as-const
const nodeName$2 = 'BxfPillIcon';

const classes$2 = {
    INLINE_MENU: 'bxf-pill__inline-menu',
};

const classes$1 = {
    INPUT_CONTAINER: 'bxf-pill__input-container',
    INPUT: 'bxf-pill__input',
};
const labels$1 = {
    INPUT_LABEL: 'Edit Pill',
};
const nodeName$1 = 'BxfPillInput';
const nodeNameInputContainer = 'BxfPillInputContainer';

const classes = {
    SIDE_BUTTON: 'bxf-pill__side-button',
    SIDE_BUTTON_ICON: 'bxf-pill__side-button-icon',
};
// eslint-disable-next-line @typescript-eslint/prefer-as-const
const nodeName = 'BxfPillSideButton';
const labels = {
    DELETE: 'Delete',
    LOCKED: 'Locked',
};

class BxfPillConstants {
    static COMPONENT_NAME = 'bxf-pill';
    static STRING = {
        ...classes$4,
        ...classes$1,
        ...classes,
        ...classes$5,
        ...classes$3,
        ...classes$2,
        TARGET: 'bxf-pill__target',
        PILL_VALIDATION_ERROR: 'bxf-pill__validation-error',
        PILL_VALIDATION_WARNING: 'bxf-pill__validation-warning',
    };
    static EVENTS = {
        CLICK_CLEAR: 'clickClear',
        CHANGE: 'change',
    };
}

class BxfPillboxConstants {
    static STRING = {
        CONTAINER: 'bxf-pillbox__container',
        CONTAINER_INTERACTIVE: 'bxf-pillbox__container-editable-input',
        PILL_CONTAINER: 'bxf-pillbox__pill-container',
        PILL: 'bxf-pillbox__pill',
        LABEL: 'bxf-pillbox__label',
        CLEAR_ALL: 'bxf-pillbox__clear-all',
        INPUT: 'bxf-pillbox__input',
        TOP_CONTAINER: 'bxf-pillbox__top-container',
        OVERFLOW_CONTAINER: 'bxf-pillbox__overflow-container',
        TRUNCATION_OVERFLOW: 'bxf-pillbox__truncation-overflow',
        CLEAR_ALL_TEXT: 'Clear all',
        LIST_WRAPPER: 'bxf-pillbox__list-wrapper',
        LIST_WRAPPER_FACETED_FILTER: 'bxf-pillbox__list-wrapper--faceted-filter',
        LIST_ITEM_WRAPPER: 'bxf-pillbox__list-item-wrapper',
        LIST_ITEM_WRAPPER_FACETED_FILTER: 'bxf-pillbox__list-item-wrapper--faceted-filter',
        LIST_ITEM_WRAPPER_TRUNCATED: 'bxf-pillbox__list-item-wrapper--truncated',
        ERROR_MESSAGE_CLASS: 'bxf-pillbox__message--error',
        WARNING_MESSAGE_CLASS: 'bxf-pillbox__message--warning',
        PILLBOX_VALIDATION_ERROR: 'bxf-pillbox__validation-error',
        PILLBOX_VALIDATION_WARNING: 'bxf-pillbox__validation-warning',
        ERROR_MESSAGE: 'Error Message',
        PILLBOX_LABEL_ASTERISK: 'bxf-pillbox__label-asterisk',
    };
    static PILLBOX_CONTAINER_ROLE = {
        EDITABLE: 'group',
    };
}

export { BxfPillConstants as B, BxfPillboxConstants as a, getNodeByBxfNodeName as b, getState as c, addGlobalEventListener as d, closestShadowDom as e, getFocusSource as f, getDataProps as g, classes$5 as h, constants as i, classes$3 as j, classes$2 as k, classes$1 as l, nodeName$1 as m, nodeName$2 as n, nodeNameInputContainer as o, nodeName as p, classes as q, removeGlobalEventListener as r, setState as s, labels$1 as t, labels as u, withState as w };
